import { useCallback, useMemo } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useModals, ModalNames } from 'components/modals';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { detectLoyaltyIntegration, Integrations, Statuses } from 'src/components/loyalty/use-loyalty';
import { formatBirthdayForRewards } from 'shared/helpers/rewards';
import { GetDutchiePosLoyaltyConfigDocument, GetRewardsV2Document } from 'src/gql/graphql';

const EXCLUDED_VARIANTS: InterfaceVariants[] = [
  InterfaceVariants.kiosk,
  InterfaceVariants.mobileEcomm,
  InterfaceVariants.plus,
];

export type TriggerPostSignupModalSequenceParams = {
  skipPostSignupSequence?: boolean;
  customerId?: string;
  phone?: string;
  birthday?: string;
};

export type UsePostSignupModalSequenceResult = {
  triggerPostSignupModalSequence: (params?: TriggerPostSignupModalSequenceParams) => Promise<void>;
};

export function usePostSignupModalSequence(): UsePostSignupModalSequenceResult {
  const { openModal } = useModals();
  const flags = useFlags();
  const { variant } = useUI();
  const User = useUser();
  const { dispensary } = useDispensary();
  const { isDutchiePayEnabledForDispo } = useDutchiePay();
  const apolloClient = useApolloClient();
  const dispensaryId = dispensary?.id ?? null;
  const isRolloutEnabled = flags['fintech.platform.post-signup-onboarding-modals.rollout'] ?? false;

  // Dutchie Loyalty is the only supported program for the join modal today.
  const showLoyaltyRewards = dispensary?.storeSettings?.showLoyaltyRewards ?? false;
  const isDutchieLoyaltyCandidate =
    isRolloutEnabled &&
    !EXCLUDED_VARIANTS.includes(variant) &&
    showLoyaltyRewards &&
    detectLoyaltyIntegration(dispensary) === Integrations.dutchiepos &&
    !!dispensaryId;

  const { data: dutchiePosLoyaltyConfigData, loading: dutchiePosLoyaltyConfigLoading } = useQuery(
    GetDutchiePosLoyaltyConfigDocument,
    {
      variables: { dispensaryId },
      skip: !isDutchieLoyaltyCandidate,
    }
  );

  const isDutchieLoyaltyConfigEligible =
    !dutchiePosLoyaltyConfigLoading &&
    (dutchiePosLoyaltyConfigData?.getDutchiePosLoyaltyConfig?.loyaltyRequireOptIn ?? false);

  // Bank Link eligibility is a synchronous derivation of the dispensary's payment methods
  // (via useDutchiePay), unlike loyalty's async GetRewardsV2 check above.
  const isBankLinkCandidate = isRolloutEnabled && !EXCLUDED_VARIANTS.includes(variant) && isDutchiePayEnabledForDispo;

  const shouldShowLoyaltyJoinModal = useCallback(
    async (customerId?: string, phone?: string, birthday?: string): Promise<boolean> => {
      if (!isDutchieLoyaltyCandidate || !isDutchieLoyaltyConfigEligible) {
        return false;
      }

      if (!phone || !birthday || !customerId) {
        return false;
      }

      try {
        const { data } = await apolloClient.query({
          query: GetRewardsV2Document,
          variables: {
            phoneNumber: phone,
            dispensaryId,
            dob: formatBirthdayForRewards(birthday),
            pin: '',
            customerId,
          },
          fetchPolicy: 'network-only',
        });

        return data.getRewardsV2?.isLoyaltyMember === false;
      } catch {
        return false;
      }
    },
    [isDutchieLoyaltyCandidate, isDutchieLoyaltyConfigEligible, apolloClient, dispensaryId]
  );

  const triggerPostSignupModalSequence = useCallback(
    async ({
      skipPostSignupSequence = false,
      customerId,
      phone,
      birthday,
    }: TriggerPostSignupModalSequenceParams = {}): Promise<void> => {
      if (skipPostSignupSequence || !isRolloutEnabled || EXCLUDED_VARIANTS.includes(variant)) {
        return;
      }

      // Set synchronously, before any awaited calls below, so LoyaltyAccountDetection's
      // async-gated effect can never observe this as unset for this signup.
      User.postSignupModalSequenceDispensaryId = dispensaryId;

      let joinedLoyalty = false;
      if (await shouldShowLoyaltyJoinModal(customerId, phone, birthday)) {
        const joinResult = await openModal<undefined, { status: Statuses } | undefined>(
          ModalNames.postSignupLoyaltyJoin
        );
        joinedLoyalty = joinResult?.status === Statuses.success;
      }
      if (isBankLinkCandidate) {
        await openModal(ModalNames.postSignupBankLink);
      }
      // The pass modal only fires immediately after a successful loyalty join in this same
      // sequence — never independently, and never after a skipped/ineligible/declined join.
      if (joinedLoyalty) {
        await openModal(ModalNames.postSignupLoyaltyPass);
      }
    },
    [openModal, isRolloutEnabled, variant, User, dispensaryId, shouldShowLoyaltyJoinModal, isBankLinkCandidate]
  );

  return useMemo(() => ({ triggerPostSignupModalSequence }), [triggerPostSignupModalSequence]);
}
